import React from "react";

/**
 * The form's icon set.
 *
 * Drawn rather than pulled from a font so they inherit `currentColor`, scale
 * cleanly and stay one visual family — 24px grid, 2px stroke, round caps.
 */

type IconProps = { size?: number; color?: string };

const base = (size: number, color?: string) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: color ?? "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
});

export const ChevronRight = ({ size = 14, color }: IconProps) => (
  <svg {...base(size, color)}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export const Clock = ({ size = 16, color }: IconProps) => (
  <svg {...base(size, color)}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

export const Lock = ({ size = 14, color }: IconProps) => (
  <svg {...base(size, color)}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

export const Upload = ({ size = 20, color }: IconProps) => (
  <svg {...base(size, color)}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

export const Plus = ({ size = 16, color }: IconProps) => (
  <svg {...base(size, color)}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export const Trash = ({ size = 16, color }: IconProps) => (
  <svg {...base(size, color)}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

export const Info = ({ size = 18, color }: IconProps) => (
  <svg {...base(size, color)}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

export const Alert = ({ size = 18, color }: IconProps) => (
  <svg {...base(size, color)}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

export const Calendar = ({ size = 20, color }: IconProps) => (
  <svg {...base(size, color)}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

export const Bars = ({ size = 20, color }: IconProps) => (
  <svg {...base(size, color)}>
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

export const Check = ({ size = 20, color }: IconProps) => (
  <svg {...base(size, color)}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

export const Eye = ({ size = 20, color }: IconProps) => (
  <svg {...base(size, color)}>
    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const FileText = ({ size = 20, color }: IconProps) => (
  <svg {...base(size, color)}>
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <path d="M14 3v6h6" />
  </svg>
);

export const Details = ({ size = 20, color }: IconProps) => (
  <svg {...base(size, color)}>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M8 8h8M8 12h8M8 16h5" />
  </svg>
);

export const Download = ({ size = 16, color }: IconProps) => (
  <svg {...base(size, color)}>
    <path d="M12 4v11" />
    <path d="M7 11l5 5 5-5" />
    <path d="M5 20h14" />
  </svg>
);

export const Pencil = ({ size = 16, color }: IconProps) => (
  <svg {...base(size, color)}>
    <path d="M4 20h4L19 9l-4-4L4 16v4z" />
    <path d="M14 6l4 4" />
  </svg>
);

export const Close = ({ size = 20, color }: IconProps) => (
  <svg {...base(size, color)}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);
